import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUsers, 
  FaChevronLeft, 
  FaChevronRight, 
  FaFile,
  FaSearch,
  FaFilter,
  FaTimes,
  FaMoneyBillWave,
  FaFileContract
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import styles from './ProjectsFinanceManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BudgetModal from './BudgetModal';
import ContractsModal from './ContractsModal';
import TableFilter from '../../../components/TableFilter';
import { useTheme } from '../../../contexts/ThemeContext';
import './ProjectsFinanceManagementDarkMode.css'; // Import dark mode styles

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const formatTimestamp = (timestamp) => {
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
  }
  return timestamp || '';
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && value.seconds) return formatTimestamp(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const ProjectsFinanceManagementPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filters, setFilters] = useState({});
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);
  
  // Define visible columns
  const [visibleColumns, setVisibleColumns] = useState({
    customer: true,
    titleShort: true,
    ethicsCommitteeDate: true,
    ethicsCommitteeNumber: true,
    ethicsCommitteePlace: true,
    firstInvoiceDate: true,
    paymentFrequency: true,
    projectCentersType: true,
    projectDate: true,
    projectDepartment: true,
    projectDueDate: true,
    projectPhase: true,
    coordinatorAcademician: true,
    protocolNumber: true,
    sbFirstApprovalDate: false,
    sbFirstApprovalNumber: false,
    sbProjectNumber: false,
    titleLong: false
  });

  // Column definitions
  const columns = {
    customer: { label: 'Müşteri', type: 'text' },
    titleShort: { label: 'Başlık (Kısa)', type: 'text' },
    ethicsCommitteeDate: { label: 'Etik Kurul Tarihi', type: 'date' },
    ethicsCommitteeNumber: { label: 'Etik Kurul Numarası', type: 'text' },
    ethicsCommitteePlace: { label: 'Etik Kurul Yeri', type: 'text' },
    firstInvoiceDate: { label: 'İlk Fatura Tarihi', type: 'date' },
    paymentFrequency: { label: 'Ödeme Sıklığı', type: 'text' },
    projectCentersType: { label: 'Proje Merkezi Tipi', type: 'text' },
    projectDate: { label: 'Proje Tarihi', type: 'date' },
    projectDepartment: { label: 'Proje Departmanı', type: 'text' },
    projectDueDate: { label: 'Proje Bitiş Tarihi', type: 'date' },
    projectPhase: { 
      label: 'Proje Aşaması', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "Başlangıç", label: "Başlangıç" },
        { value: "Aktif", label: "Aktif" },
        { value: "Devam Ediyor", label: "Aktif (Eski)" },
        { value: "Pasif", label: "Pasif" },
        { value: "Tamamlandı", label: "Pasif (Eski)" }
      ]
    },
    coordinatorAcademician: { label: 'Koordinatör Akademisyen', type: 'text' },
    protocolNumber: { label: 'Protokol Numarası', type: 'text' },
    sbFirstApprovalDate: { label: 'SB İlk Onay Tarihi', type: 'date' },
    sbFirstApprovalNumber: { label: 'SB İlk Onay Numarası', type: 'text' },
    sbProjectNumber: { label: 'SB Proje Numarası', type: 'text' },
    titleLong: { label: 'Başlık (Uzun)', type: 'text' }
  };

  const fetchProjects = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Projects'));
      const projectsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...Object.entries(data).reduce((acc, [key, value]) => {
            acc[key] = formatValue(value);
            return acc;
          }, {})
        };
      });
      setProjects(projectsData);
      setFilteredProjects(projectsData);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Projeler yüklenirken hata oluştu!');
    }
  };

  useEffect(() => {
    fetchProjects();
    
    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    let filtered = projects;

    // Apply search term first
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        project => 
          (project.customer && project.customer.toString().toLowerCase().includes(lowercasedSearch)) ||
          (project.titleShort && project.titleShort.toString().toLowerCase().includes(lowercasedSearch)) ||
          (project.projectDepartment && project.projectDepartment.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'projectPhase') {
          filtered = filtered.filter(project => project[key] === filters[key]);
        } else if (key === 'projectDate' || key === 'projectDueDate' || key === 'ethicsCommitteeDate' || key === 'firstInvoiceDate') {
          filtered = filtered.filter(project =>
            project[key] && project[key].includes(filters[key])
          );
        } else {
          filtered = filtered.filter(project =>
            String(project[key] || '')
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredProjects(filtered);
  }, [projects, filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const activeCount = projects.filter(project => {
    const phase = project.projectPhase?.toLowerCase();
    return phase === 'aktif' || phase === 'devam ediyor';
  }).length;
  const inactiveCount = projects.filter(project => {
    const phase = project.projectPhase?.toLowerCase();
    return phase === 'pasif' || phase === 'tamamlandı';
  }).length;

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [projects, filteredProjects]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Proje Yönetimi
            </h1>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{projects.length}</p>
                  <h3 className={styles.cardTitle}>Toplam Proje</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Devam Ediyor</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>Tamamlandı</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Proje ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>{Object.keys(filters).length}</span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtrele ve Sütunları Seç</h3>
                    <button className={styles.clearFiltersBtn} onClick={clearFilters}>
                      Filtreleri Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Görünür Sütunlar</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(col => (
                        <div className={styles.columnCheckbox} key={col}>
                          <input
                            type="checkbox"
                            id={`col-${col}`}
                            checked={visibleColumns[col] || false}
                            onChange={() => toggleColumnVisibility(col)}
                          />
                          <label htmlFor={`col-${col}`}>{columns[col].label}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedProject && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedProjectInfo}>
              <h3 className={styles.selectedName}>
                {selectedProject.titleShort || 'Seçili Proje'}
              </h3>
              <span className={`${styles.statusBadge} ${
                ['aktif', 'devam ediyor'].includes(selectedProject.projectPhase?.toLowerCase())
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedProject.projectPhase || 'Belirtilmemiş'}
              </span>
            </div>
            <div className={styles.actionButtons}>
              <button 
                className={`${styles.actionButton} ${styles.budgetButton}`} 
                onClick={() => setShowBudgetModal(true)}
              >
                <FaMoneyBillWave className={styles.actionIcon} />
                <span>Bütçe</span>
              </button>
              <button 
                className={`${styles.actionButton} ${styles.contractsButton}`} 
                onClick={() => setShowContractsModal(true)}
              >
                <FaFileContract className={styles.actionIcon} />
                <span>Sözleşmeler</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}>
                  <thead>
                    <tr>
                      {Object.keys(columns).map(colKey => 
                        visibleColumns[colKey] && (
                          <th key={colKey} className={colKey === 'projectPhase' ? styles.statusColumn : ''}>
                            <div className={styles.columnHeader}>
                              <span>{columns[colKey].label}</span>
                              <TableFilter 
                                column={colKey} 
                                type={columns[colKey].type} 
                                options={columns[colKey].options}
                                onFilter={handleFilter}
                                currentValue={filters[colKey] || ''}
                              />
                            </div>
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.length > 0 ? (
                      filteredProjects.map((project) => (
                        !project.isEmpty && (
                          <tr 
                            key={project.id} 
                            onClick={() => handleProjectSelect(project)}
                            className={selectedProject && selectedProject.id === project.id ? styles.selectedRow : ''}
                          >
                            {Object.keys(columns).map(colKey => 
                              visibleColumns[colKey] && (
                                <td 
                                  key={colKey} 
                                  title={project[colKey]}
                                  className={colKey === 'projectPhase' ? styles.statusColumn : ''}
                                >
                                  <div className={styles.tdContentWrapper}>
                                    {colKey === 'projectPhase' ? (
                                      <span className={`${styles.statusBadge} ${
                                        project[colKey] === undefined || project[colKey] === null
                                          ? styles.statusNoData
                                          : (() => {
                                              const phaseValue = project[colKey]?.toLowerCase();
                                              const isActive = ['aktif', 'devam ediyor'].includes(phaseValue);
                                              return isActive ? styles.statusActive : styles.statusInactive;
                                            })()
                                      }`}>
                                        {project[colKey] === undefined || project[colKey] === null
                                          ? '-'
                                          : truncateText(project[colKey])}
                                      </span>
                                    ) : (
                                      <div className={styles.cellContent}>
                                        {truncateText(project[colKey], colKey === 'titleShort' || colKey === 'titleLong' ? 40 : 15)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              )
                            )}
                          </tr>
                        )
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaFile className={styles.noDataIcon} />
                            <p>Filtrelere uygun proje bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {showBudgetModal && selectedProject && (
          <BudgetModal
            project={selectedProject}
            onClose={() => setShowBudgetModal(false)}
          />
        )}

        {showContractsModal && selectedProject && (
          <ContractsModal
            project={selectedProject}
            onClose={() => setShowContractsModal(false)}
          />
        )}

        <ToastContainer />
      </div>
    </div>
  );
};

export default ProjectsFinanceManagementPage;